export interface DefaultNetworkRingProps {
  online: number;
  total: number;
  faulted?: number;
  size?: number;
}

const R = 80;
const C = 2 * Math.PI * R;

/**
 * Default-theme network status ring. Clean green arc on a muted track,
 * percentage in the middle, two counts under it (online / faulted).
 * Mirrors `DefaultChargingRing` structurally so the two feel like a pair.
 */
export function DefaultNetworkRing({
  online,
  total,
  faulted = 0,
  size = 220,
}: DefaultNetworkRingProps) {
  const pct = total === 0 ? 0 : (online / total) * 100;
  const end = (C * pct) / 100;

  return (
    <div
      style={{
        background: 'var(--evi-surface)',
        border: '1px solid var(--evi-border)',
        borderRadius: 'var(--evi-radius-lg)',
        padding: '16px',
        boxShadow: 'var(--evi-card-glow)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          color: 'var(--evi-text-mute)',
          fontWeight: 500,
          marginBottom: 8,
        }}
      >
        Network status
      </div>

      <div style={{ width: '100%', maxWidth: size, margin: '0 auto' }}>
        <svg
          viewBox="0 0 200 200"
          width="100%"
          role="img"
          aria-label={`${online} of ${total} chargers online`}
        >
          <circle cx="100" cy="100" r={R} fill="none" stroke="var(--evi-border)" strokeWidth="7" opacity="0.5" />
          <circle
            cx="100"
            cy="100"
            r={R}
            fill="none"
            stroke="var(--evi-good)"
            strokeWidth="7"
            strokeLinecap="round"
            style={{
              transform: 'rotate(-90deg)',
              transformOrigin: '100px 100px',
              strokeDasharray: `${end.toFixed(2)} ${(C - end).toFixed(2)}`,
              transition: 'stroke-dasharray 1s var(--evi-motion-fill)',
            }}
          />
          <text
            x="100"
            y="106"
            textAnchor="middle"
            fill="var(--evi-text)"
            fontSize="40"
            fontWeight="400"
            letterSpacing="-1"
          >
            {Math.round(pct)}
          </text>
          <text x="138" y="84" textAnchor="middle" fill="var(--evi-good)" fontSize="13">
            %
          </text>
          <text
            x="100"
            y="128"
            textAnchor="middle"
            fill="var(--evi-text-mute)"
            fontSize="9"
            letterSpacing="2"
          >
            ONLINE
          </text>
        </svg>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
          marginTop: 12,
          paddingTop: 12,
          borderTop: '1px solid var(--evi-border)',
        }}
      >
        <Count label="Chargers" value={`${online}/${total}`} />
        <Count
          label="Faulted"
          value={String(faulted)}
          tone={faulted > 0 ? 'warn' : 'mute'}
        />
      </div>
    </div>
  );
}

function Count({
  label,
  value,
  tone = 'mute',
}: {
  label: string;
  value: string;
  tone?: 'warn' | 'mute';
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 10,
          letterSpacing: 0.8,
          color: 'var(--evi-text-mute)',
          textTransform: 'uppercase',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: tone === 'warn' ? 'var(--evi-warn)' : 'var(--evi-text)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}
